<template>
  <div class="search-page">
    <DataView :value="searchResults">
      <template #header>
        <div class="flex justify-content-end">
          <IconField iconPosition="left">
            <AutoComplete
                id="search-page-search"
                v-model="searchQuery"
                :suggestions="autoCompleteResults"
                @itemSelect="onSelect"
                @keyup.enter="performSearch"
                placeholder="Search All Slides"
                @complete="getSearchResults"
                style="font-size: 14px;"
                optionLabel="label"
            >
              <template #option="{option}">
                  <div>{{ option.label }}</div>
                <div style="font-size: 12px; color: #757575;">Updated by: {{ option.authorName}}</div>
                <div style="font-size: 12px; color: #757575;">Value Model: {{ option.valueModelName }}</div>
              </template>
            </AutoComplete>
            <InputIcon class="pi pi-search"></InputIcon>
          </IconField>
          <div class="filter-container">
            <div class="filter-group">
              <label for="last-modified-dropdown">Last Updated:</label>
              <Dropdown id="last-modified-dropdown" v-model="selectedFilters.lastModified" :options="filterOptions.lastModified"
                        @change="onFilterChange('lastModified', $event.value)" class="confluence-dropdown">
              </Dropdown>
            </div>
            <div class="filter-group">
              <label for="author-dropdown">Author:</label>
              <Dropdown id="author-dropdown" v-model="selectedFilters.author" :options="filterOptions.author"
                        @change="onFilterChange('author', $event.value)" class="confluence-dropdown">
              </Dropdown>
            </div>
          </div>
        </div>
      </template>
      <template #list="slotProps">
        <div class="flex flex-col">
          <div v-for="(item, index) in slotProps.items" :key="index">
            <RouterLink :to="{ name: 'editor', query: { id: item.id } }">
            <div class="bg-surface-100 p-1" style="border-radius: 10px">
              <div class="route bg-surface-0 flex items-center gap-2 justify-center py-1 px-2" style="border-radius: 10px; border: 1px solid #dfe1e6; background-color: #ffff; box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.04), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)">
                <div class="text-lg font-medium mt-1">{{ item.name }}</div>
                <div style="font-size: 12px; color: #757575;">Updated {{getReadableDate(item.updated)}} by {{ item.authorName}}</div>
                <div style="font-size: 12px; color: #757575;">{{ item.valueModelName }}</div>
                </div>
              </div>
            </RouterLink>
            </div>
        </div>

      </template>
      <template #empty>
        <div v-if="isLoading">
          <div class="card flex justify-center">
            <ProgressSpinner style="width: 30px; height: 30px" strokeWidth="6" fill="transparent"
                             animationDuration="1s" aria-label="Custom ProgressSpinner" />
          </div>
        </div>
        <div v-else>No results</div>
      </template>
    </DataView>
  </div>
</template>

<script setup lang="ts">
import {onMounted, ref, watch} from 'vue'
import {useRoute, useRouter} from "vue-router";
import {useSearch} from "@/hooks/useSearchService";
import Image from '@/assets/images/library/Image.png'


const {searchQuery, autoCompleteResults, searchResults, getSearchResults, isLoading, getAuthors} = useSearch();
const route = useRoute();
const router = useRouter();
const authors = ref([])


const layout = ref("grid");

const filterOptions = ref({
  lastModified: ['Anytime', 'Today', 'Yesterday', 'Past 7 days', 'Past 30 days', 'Past Year'],
  author: ['All']
})

const selectedFilters = ref({
  lastModified: 'Anytime',
  author: 'All'
})

const performSearch = () => {
  if (searchQuery.value.trim() === "") return;
  router.push({path: '/search', query: {q: searchQuery.value}});
};


const onSelect = (event) => {

  router.push({name: 'editor', query: {id: event.value, vmId: event.vmId}});
};

const getValidFilters = () => {
  return Object.fromEntries(
      Object.entries(selectedFilters.value).filter(([key, value]) => value != null)
  );
};


const loadLazyData = async () => {
  const validFilters = getValidFilters();
  await getSearchResults(searchQuery.value, validFilters);
}


const onFilterChange = (filterType, filterValue) => {

  selectedFilters.value[filterType] = filterValue
  loadLazyData()
}

const getReadableDate = (isoDate) => {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

onMounted(async () => {
  if (route.query.q) {
    searchQuery.value = route.query.q as string;
    await getSearchResults(searchQuery.value, null);

    authors.value = await getAuthors();
    filterOptions.value.author = ['All', ...authors.value];
  }
});

watch(() => route.query.q, (newQuery) => {
  if (newQuery) {
    searchQuery.value = newQuery as string;
    getSearchResults(searchQuery.value, null);
  }
});


</script>
<style>
#search-page-search .p-inputtext {
  padding-left: 35px;
  border-radius: 5px;
  width: 1125px;
}


</style>


<style scoped>

.search-page {
  width: 100%;
  height: 100vh;
}

.filter-container {
  display: flex;
  gap: 20px;
  margin-top: 10px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 10px;
}


.grid {
  width: 320px !important;
}

:deep(.p-dataview-content) {
  background: transparent !important;
}


:deep(.p-icon) {
    width: .8rem;
    height: .8rem;
}

:deep(.p-inputtext) {
  font-size: 1rem;
  padding: 0.3rem 0.3rem;
}

:deep(.card) {
  background-color: transparent;
  border: none;
}

.route:hover {
  background-color: #f0f0f0 !important;
  cursor: pointer !important;
}
</style>
